/**
 * JSX components for instruction composition.
 *
 * Components are organized into:
 * 1. Content blocks - reusable patterns for tool guidance, examples, errors
 * 2. Control flow - conditional rendering helpers
 *
 * For dynamic section tags, use JSX intrinsic `<tag-*>` elements directly
 * (e.g., `<tag-identity>`, `<tag-constraints>`). The reconciler handles these natively.
 */

import { encode } from '@toon-format/toon';
import type { ReactNode } from 'react';

import type { ColumnSchema } from '#core/lib/transforms/column-schema.ts';

import type { ToolComponent } from '#plugin-chat/lib/tool/message-formatters.ts';

/** Renders a value as pretty-printed JSON in a fenced code block. */
export function Json({ value }: { value: unknown }) {
  return <pre>{JSON.stringify(value, null, 2)}</pre>;
}

export function Toon({ value }: { value: unknown }) {
  return <pre>{encode(value)}</pre>;
}

interface HeaderedBulletListProps {
  /** Section header content (`<header>X</header>` renders as `**X:**`). */
  header: ReactNode;
  /** Bullet items. Returns null when empty/undefined so callers can pass
   *  optional rule arrays without conditional wrapping. */
  items: readonly string[] | undefined;
}

/**
 * A `<header>` + `<ul>` pair — the shared shape used by every "labeled rule
 * list" in this file (Always/Never in {@link RuleList}, When-to-use /
 * When-not-to-use in {@link ToolGuidance}). Callers pass already-formatted
 * strings; bullets pass through to the reconciler verbatim, so inline
 * markdown (backticks, etc.) works.
 */
export function HeaderedBulletList({ header, items }: HeaderedBulletListProps) {
  if (!items || items.length === 0) {
    return null;
  }
  return (
    <>
      <header>{header}</header>
      <ul>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </>
  );
}

interface ToolGuidanceProps {
  // biome-ignore lint/suspicious/noExplicitAny: Not checked
  tool: ToolComponent<any>;
  description?: string;
  whenToUse: readonly string[];
  whenNotToUse?: readonly string[];
  /** Additional tool-specific guidance rendered after the when-to/not-to sections. */
  children?: ReactNode;
}

/**
 * Reusable block for tool usage guidance with when-to-use/not-to-use patterns.
 *
 * `children` render after the when-to/not-to sections and are intended for
 * tool-specific guidance that doesn't fit the structured when-to-use pattern
 * (procedures, parameter dictionaries, output notes, etc.).
 */
export function ToolGuidance({ tool, description, whenToUse, whenNotToUse, children }: ToolGuidanceProps) {
  return (
    <>
      {description && <p>{description}</p>}
      <HeaderedBulletList header={<>When to use {tool.mention}</>} items={whenToUse} />
      <HeaderedBulletList header={<>When not to use {tool.mention}</>} items={whenNotToUse} />
      {children}
    </>
  );
}

interface QueryExampleProps {
  user: string;
  steps: readonly string[];
  name?: string;
}

/**
 * Reusable block for query workflow examples showing user request and assistant steps.
 */
export function QueryExample({ user, steps, name = 'query' }: QueryExampleProps) {
  const stepsText = steps.map((step) => `Assistant: ${step}`).join('\n');
  return <example name={name}>{`User: "${user}"\n${stepsText}`}</example>;
}

interface RuleListProps {
  always?: readonly string[];
  never?: readonly string[];
  /** Optional trailing content rendered after the lists. Use for caveats or
   *  scope notes that don't fit in a single bullet. */
  note?: ReactNode;
}

/**
 * Renders an `Always` / `Never` rule pair with an optional trailing note.
 *
 * Use this for behavioral sections where the model must distinguish required
 * actions from forbidden ones — output discipline, tool-strategy guardrails,
 * etc. The shared shape lets the model recognize all rule sections as the
 * same construct and reduces drift across prompts.
 *
 * Bullet content may include inline markdown (e.g. backticks for code) — the
 * reconciler passes strings through verbatim into the rendered list.
 */
export function RuleList({ always, never, note }: RuleListProps) {
  return (
    <>
      <HeaderedBulletList header="Always" items={always} />
      <HeaderedBulletList header="Never" items={never} />
      {note && <p>{note}</p>}
    </>
  );
}

interface ColumnSchemaTableProps {
  columns: readonly ColumnSchema[];
  caption?: ReactNode;
}

/**
 * Render a `Column | Type | Description` markdown table from a list of
 * {@link ColumnSchema} entries. Used by tools that surface dataset / view
 * schemas in their LLM-facing result message.
 */
export function ColumnSchemaTable({ columns, caption }: ColumnSchemaTableProps) {
  if (!columns || columns.length === 0) {
    return null;
  }
  return (
    <>
      {caption}
      <table>
        <thead>
          <tr>
            <th>Column</th>
            <th>Type</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {columns.map((c) => (
            <tr key={c.name}>
              <td>{c.name}</td>
              <td>{c.type === 'ref' ? `ref<${c.refKind ?? 'unknown'}>` : c.type}</td>
              <td>{c.description ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
